// assistantThreadUrl.js — EVERY MAIN-THREAD CONVERSATION HAS ITS OWN URL
// (Nika 2026-09-30: "also add unique url to those thread in main chatbot so we
// can identify those again").
//
// THE SHAPE: `?ai=<conversationId>` on the route the main thread shows over —
// `/w/<org>/<ws>/?ai=…` on the home, `/w/<org>/<ws>/crm?ai=…` over a page
// (main thread = "Everywhere", AI_ASSISTANT_SPEC §12.2). A query key, not a
// path segment: the main thread is a VIEW of the route it opened on
// (`_mainThreadPath`), so the route stays the route. Not `thread=`: the
// address bar already mirrors a PageSlot pane's own query
// (`/?page=/mail&thread=…`), and chat and add-app read that name.
// The URL keeps the address bar's own `/w/<org>/<ws>` prefix, so a pasted link
// opens in the right workspace — on a canvas project page too
// (`/w/<org>/<ws>/p/<key>?ai=…`, see assistantThreadUrlWrite).
//
// WHY: the open thread was remembered PER DEVICE (functions/_simone/
// threadPointer.js — localStorage, 24 h), so another device, or the same one a
// day later, showed no conversation. The URL names the thread for a reload,
// another device and a pasted link. The pointer STAYS: it still serves the
// drawer, the dashboard and add-app threads, the agent threads and the
// in-flight flag (the interrupted-turn notice) — the URL wins for the main
// thread and the pointer follows it (switchSession writes it).
//
// ONE RECONCILE, TWO DIRECTIONS (components/AssistantThreadUrlSync.js runs it on
// every change of the keys it reads). `root._aiThreadUrlId` is the id the URL
// and the state last AGREED on:
//   • the ADDRESS BAR moved away from it (a pasted link, a reload, Back /
//     Forward) → the URL leads: open that conversation, or close the thread
//     the URL no longer names;
//   • the STATE moved away from it (the first real id of a new conversation,
//     a history switch, "Open in main", Esc, "Open in sidebar", New chat, a
//     navigation, a placement change) → the URL follows. A thread that GAINS
//     its URL or changes it is a new history entry (push: Back returns to the
//     page without the thread); a thread that LOSES it rewrites the entry
//     (replace: never stack an entry for what the person just closed).
// A local `s-…` session names no server conversation and never reaches the URL.
//
// ACCESS. The server decides: `getWorkspaceConversation` reads a conversation
// for its owner, or for a workspace member once the owner shared it, and
// answers the SAME 404 for an unshared and a missing one (the route middleware
// checked the membership first). The client only reports it: a link that
// cannot be read loads NO rows, the thread view says "not available"
// (`root._aiThreadLink.status === 'unavailable'`; the sidebar placement gets a
// notification), and the next send starts a NEW conversation — never a hidden
// one under an empty view. The line itself is components/
// AssistantThreadUnavailable.js, mounted in the thread view above the empty hint.
//
// PLACEMENT (Nika's pick): the default placement opens the link in MAIN, over
// the route in the URL; a person who chose the sidebar gets it in the sidebar.
// The choice is never rewritten.
//
// Navigation goes through `el.router` — never a raw history or location write.
//
// THE ENGINE RE-RUNS THE RECONCILE INSIDE ITS OWN WRITES. The sentinel calls it
// from a `subscribeTo` effect, which tracks every root key the reconcile
// reads: a root write made here (a marker, the thread's flag) flushes that
// effect again — at once when the write comes from outside an effect (the
// link's async read), after the current run otherwise. And the router can
// write the history entry LATER than the call (the default router awaits its
// guards before the write; only the workspace-prefixed branch writes at once).
// So every step keeps each intermediate state readable as "nothing to do":
//   • while a link is decided its marker says `loading` — the final marker
//     write is the LAST write of the step;
//   • a URL write states what the address bar WILL show before anything else
//     moves (`el.__aiThreadUrlExpect`, assistantThreadUrlCommit); until the
//     bar shows it the reconcile stands still.
//
// Frank note: el.call serializes each registered export on its own (FA204),
// so every export here is self-contained and reaches its siblings by el.call.

// PURE — the app URL (path + query + hash) that names conversation `id`. A
// falsy id removes the key. Every other parameter and the hash stay as they are.
export const assistantThreadUrlNext = function assistantThreadUrlNext(route, params, hash, id) {
  const path = typeof route === 'string' && route ? route : '/'
  const query = new URLSearchParams(params || '')
  if (id) query.set('ai', String(id))
  else query.delete('ai')
  const qs = query.toString()
  return path + (qs ? '?' + qs : '') + (typeof hash === 'string' ? hash : '')
}

// A conversation id a link can carry: a saved one — never a local draft
// ('s-…'), which no other tab or member can open.
export const assistantThreadIdLinkable = function assistantThreadIdLinkable(id) {
  return typeof id === 'string' && !!id && !id.startsWith('s-')
}

// The conversation the address bar names, or null. Read at the shell root: an
// element inside a PageSlot pane would read its pane's own route.
export const assistantThreadLinkId = function assistantThreadLinkId() {
  const app = typeof this.getRoot === 'function' ? this.getRoot() : this
  if (!app || typeof app.call !== 'function') return null
  const v = app.call('readPageParam', 'ai')
  return this.call('assistantThreadIdLinkable', v) ? v : null
}

// The conversation the URL SHOULD name for this state: the one the main thread
// shows, once it is a server conversation.
export const assistantThreadUrlTarget = function assistantThreadUrlTarget(root) {
  const r = root && typeof root === 'object' ? root : {}
  const id = r.activeSessionId
  const open = !!this.call('assistantMainThreadOpen', r)
  return open && this.call('assistantThreadIdLinkable', id) ? id : null
}

// Write the URL for conversation `id` (null = no thread) on the CURRENT route.
// Only the `ai` key changes: the router gets the address bar's OWN path, its
// workspace prefix included. The workspace router keeps a prefix the caller
// wrote (shared planMwtNav), and it adds one to an intranet route only — so
// a stripped canvas project path went out bare: `/p/velvet-lantern?ai=…`
// instead of `/w/<org>/<ws>/p/velvet-lantern?ai=…` (dev 2026-10-30, after a
// Fly "Do it" in a canvas project), and every later read of the project from
// the URL took `p` for its owner.
// Returns what the router returns — a promise that settles when the navigation
// is done (the entry written, or refused).
export const assistantThreadUrlWrite = function assistantThreadUrlWrite(id, replace) {
  const app = typeof this.getRoot === 'function' ? this.getRoot() : this
  if (!app || typeof app.call !== 'function' || typeof this.router !== 'function') return null
  const live = app.call('readPageRoute')
  const route = typeof live === 'string' && live ? live : '/'
  const next = this.call(
    'assistantThreadUrlNext',
    route,
    app.call('readPageParams'),
    app.call('readPageHash'),
    id || null
  )
  return this.router(next, app, {}, { scrollToTop: false, replace: !!replace })
}

// MAKE THE ADDRESS BAR NAME `id` (null = no thread), and record that both
// sides agree on it. One step for every branch that writes the URL:
//   1. the expectation first — what the bar will show;
//   2. the markers (`_aiThreadUrlId`, no link);
//   3. the router.
// A reconcile that runs in between (see the engine note above) reads the
// expectation and stands still. It is lifted when the bar shows the id, or
// when the router is done without it (a refused navigation) — then the
// reconcile runs once more and the address bar leads.
export const assistantThreadUrlCommit = function assistantThreadUrlCommit(id, replace) {
  const root = this && this.state && this.state.root
  if (!root || typeof root.update !== 'function') return false
  const want = id || null
  const expect = { id: want }
  this.__aiThreadUrlExpect = expect
  if ((root._aiThreadUrlId || null) !== want || root._aiThreadLink) {
    root.update({ _aiThreadUrlId: want, _aiThreadLink: null }, { preventFetch: true })
  }
  let done = null
  try {
    done = this.call('assistantThreadUrlWrite', want, replace)
  } catch (_) {
    done = null
  }
  const lift = () => {
    if (this.__aiThreadUrlExpect !== expect) return false
    this.__aiThreadUrlExpect = null
    return true
  }
  if (this.call('assistantThreadLinkId') === want) {
    lift()
  } else if (done && typeof done.then === 'function') {
    const settle = () => {
      if (lift()) this.call('assistantThreadUrlReconcile')
    }
    done.then(settle, settle)
  } else {
    // no router, or one that wrote nothing: nothing is on its way
    lift()
  }
  return true
}

// Does the thread view say "not available"? While the link that could not be
// read is the state and nothing is asked yet: the first question of the new
// conversation takes the line away with the empty hint, in the same paint —
// the rows never shift under it (Rule 66). Both keys are read up front so a
// reactive caller subscribes to both, whichever answers.
export const assistantThreadUnavailable = function assistantThreadUnavailable(root) {
  const r = root && typeof root === 'object' ? root : {}
  const link = r._aiThreadLink
  const list = r.chatMessages
  const empty = !Array.isArray(list) || list.length === 0
  return !!(link && link.status === 'unavailable') && empty
}

// THE URL LEADS, a conversation: load it, then show it where this person reads
// the assistant. `_aiThreadLink` marks the link while it loads (the reconcile
// stands still) and when it cannot be read — with the WORKSPACE the read ran
// in (`ws`): a conversation belongs to one workspace, so an answer is good
// for that workspace only (the reconcile asks again when the shell moves).
export const assistantThreadLinkOpen = async function assistantThreadLinkOpen(id) {
  const root = this && this.state && this.state.root
  if (!root || typeof root.update !== 'function' || !id) return false
  const ws = (root.workspace && (root.workspace.id || root.workspace._id)) || null
  root.update(
    { _aiThreadUrlId: id, _aiThreadLink: { id, status: 'loading', ws } },
    { preventFetch: true }
  )
  try {
    // When the link names the thread this device remembers, the restore brings
    // it back with its mid-turn notice and its waiting client call; it stands
    // still for any other remembered thread (sessions.js restoreActiveThread:
    // the address bar wins). Every other id is a plain server read. Both leave
    // `activeSessionId` alone when the read fails.
    if (!root.activeSessionId) await this.call('restoreActiveThread')
    if (root.activeSessionId !== id) await this.call('switchSession', id)
  } catch (_) {
    /* a failed read is the not-available state below */
  }
  // The address bar or the workspace moved on while the conversation loaded:
  // the attempt that started after this one owns the state now.
  const mine = root._aiThreadLink
  if (!mine || mine.id !== id || mine.status !== 'loading' || (mine.ws || null) !== ws) {
    return false
  }
  const main = root.assistantDock === 'main' && !root._canvasSlot && !root._canvasSlotSecond

  // The thread (or the sidebar) shows FIRST and the marker goes LAST: while
  // it says `loading` the reconcile stands still, so no half-open state is
  // read as a change of the thread.
  if (root.activeSessionId === id) {
    if (main) {
      this.call('assistantThreadToMain')
      root.update({ _aiThreadLink: null }, { preventFetch: true })
    } else {
      root.update({ assistantOpen: true })
      // The sidebar shows it: the URL names MAIN threads only.
      this.call('assistantThreadUrlCommit', null, true)
    }
    this.call('assistantThreadUrlReconcile')
    return true
  }

  // NOT AVAILABLE — no rows of it, and none of another conversation under the
  // message: the next send starts a new one.
  if (main) {
    root.update({
      activeSessionId: null,
      chatMessages: [],
      chatTyping: false,
      currentAssistantId: null
    })
    this.call('assistantThreadToMain')
    root.update({ _aiThreadLink: { id, status: 'unavailable', ws } }, { preventFetch: true })
    this.call('assistantThreadUrlReconcile')
    return false
  }
  this.call('openNotification', {
    type: 'error',
    title: this.call('polyglot', 'workspace.simone.threadUnavailableTitle'),
    message: this.call('polyglot', 'workspace.simone.threadUnavailable')
  })
  // The sidebar is closed and its chat not mounted: when the person opens it,
  // it restores the thread this device remembers, as on any load — the address
  // bar names no thread by then.
  this.call('assistantThreadUrlCommit', null, true)
  return false
}

// THE URL LEADS, no conversation (Back, or a link without one): the thread it
// named closes; the conversation stays in the sidebar and the thread chips.
export const assistantThreadLinkLeave = function assistantThreadLinkLeave() {
  const root = this && this.state && this.state.root
  if (!root || typeof root.update !== 'function') return false
  const last = root._aiThreadUrlId
  const link = root._aiThreadLink
  const showing =
    !!this.call('assistantMainThreadOpen', root) &&
    (root.activeSessionId === last || !!(link && link.id === last))
  // The thread closes first and the markers go last: a reconcile that runs in
  // between still reads "the address bar names no thread" and ends here too.
  if (showing) this.call('assistantMainThreadClose')
  if (root._aiThreadUrlId || root._aiThreadLink) {
    root.update({ _aiThreadUrlId: null, _aiThreadLink: null }, { preventFetch: true })
  }
  return true
}

// THE RECONCILE. Returns what it did (tests read it): 'open' | 'leave' |
// 'wait' | 'loading' | 'unavailable' | 'write' | 'switch' | 'pending' | null.
//
// THE WORKSPACE CAN MOVE UNDER A LIVE PAGE, and the address bar keeps its
// query when it does. Two facts decide what a conversation id on it means:
//   • the id this element last saw OPEN and agreed (`el.__aiThreadAgreed` —
//     a plain property of the sentinel: a declared `scope` is a reactive
//     store in smbls, and this memory must not re-run the effect that writes
//     it). A workspace switch wipes the thread's state and these markers
//     (WORKSPACE_SCOPED_KEYS) but not the address bar, so that id rides along
//     to the new workspace's URL. It belongs to the workspace the person left:
//     the key is removed, never opened there;
//   • the workspace a link was READ in (`_aiThreadLink.ws`). A link that was
//     never open is tried in the workspace the shell settles on — the boot can
//     still move to the workspace the URL names after the first read — so an
//     answer from another workspace is asked again, once per workspace.
export const assistantThreadUrlReconcile = function assistantThreadUrlReconcile() {
  const root = this && this.state && this.state.root
  if (!root || typeof root.update !== 'function') return null
  const ws = (root.workspace && (root.workspace.id || root.workspace._id)) || null

  const step = () => {
    const url = this.call('assistantThreadLinkId')
    const last = root._aiThreadUrlId || null
    const link = root._aiThreadLink

    // A URL write of this module is on its way (assistantThreadUrlCommit):
    // nothing is decided until the address bar shows it.
    const expect = this.__aiThreadUrlExpect
    if (expect) {
      if (url !== expect.id) return 'pending'
      this.__aiThreadUrlExpect = null
    }

    // The open thread's markers are gone and the address bar still names it:
    // the workspace changed. The thread stays behind.
    if (url && this.__aiThreadAgreed === url && url !== last) {
      this.__aiThreadAgreed = null
      this.call('assistantThreadUrlCommit', null, true)
      return 'switch'
    }

    // 1. The address bar moved: the URL leads.
    if (url !== last) {
      if (!url) {
        this.call('assistantThreadLinkLeave')
        return 'leave'
      }
      // The read needs the AI service and the workspace it reads in; the view
      // re-runs this when either lands.
      if (root._aiSvcReady !== true || !ws) return 'wait'
      this.call('assistantThreadLinkOpen', url)
      return 'open'
    }

    // 2. The state moved: the URL follows.
    if (link && link.id === url) {
      // The answer is for another workspace than the one the shell is in now.
      if (link.ws && ws && link.ws !== ws) {
        if (root._aiSvcReady !== true) return 'wait'
        this.call('assistantThreadLinkOpen', url)
        return 'open'
      }
      if (link.status === 'loading') return 'loading'
      const open = !!this.call('assistantMainThreadOpen', root)
      const next = this.call('assistantThreadUrlTarget', root)
      // The message is on screen: the dead link stays on the address bar until
      // the person closes the thread or a new conversation takes its place.
      if (open && !next) return 'unavailable'
      this.call('assistantThreadUrlCommit', next, true)
      return 'write'
    }
    const target = this.call('assistantThreadUrlTarget', root)
    if (target === last) return null
    this.call('assistantThreadUrlCommit', target, !target)
    return 'write'
  }

  const did = step()
  // What the address bar and an OPEN thread agree on now. A loading or an
  // unreadable link is not an agreement, and the memory lasts only while the
  // address bar still names that id.
  const now = this.call('assistantThreadLinkId')
  const held = root._aiThreadUrlId || null
  if (now && !root._aiThreadLink && held === now) this.__aiThreadAgreed = now
  else if (this.__aiThreadAgreed !== now) this.__aiThreadAgreed = null
  return did
}
